import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { apiFetch } from '@/lib/http';
import { Loader2, MailCheck, TriangleAlert } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { establishEmailLinkSession, cleanEmailLinkUrl } from '@/lib/authEmailLink';
import { dbg } from '@/lib/dbg';

type CallbackStage = 'confirm' | 'processing' | 'completed' | 'error';

export default function AuthCallback() {
  const [, setLocation] = useLocation();
  const [stage, setStage] = useState<CallbackStage>('confirm');
  const [confirmed, setConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const link = useRef(window.location.href);

  useEffect(() => {
    // Rendering or scanning the landing page must not consume a one-use link.
    if (!confirmed) return;
    let cancelled = false;
    const run = async () => {
      try {
        const { intent } = await establishEmailLinkSession(link.current);
        if (cancelled) return;
        cleanEmailLinkUrl();
        if (intent === 'recovery') {
          setStage('completed');
          setLocation('/reset-password');
          return;
        }

        const meRes = await apiFetch('/api/users/me', { credentials: 'omit' });
        if (cancelled) return;
        if (!meRes.ok) {
          dbg.error('[AUTH_CALLBACK] /api/users/me failed', meRes.status);
          setErrorMessage('We were unable to verify your account. Please try again.');
          setStage('error');
          return;
        }
        const mePayload = await meRes.json();
        const requirePasswordChange = Boolean(mePayload?.user?.requirePasswordChange);

        if (requirePasswordChange) {
          dbg.log('[AUTH_CALLBACK] User requires password change, redirecting to set-password');
          setStage('completed');
          setLocation('/set-password');
          return;
        }

        dbg.log('[AUTH_CALLBACK] Invitation/session complete, redirecting to landing');
        setStage('completed');
        setLocation('/');
      } catch (error) {
        if (cancelled) return;
        setErrorMessage(error instanceof Error ? error.message : 'Unable to verify your email link. Please request a new one.');
        setStage('error');
      } finally {
        if (!cancelled) cleanEmailLinkUrl();
      }
    };

    run();
    return () => { cancelled = true; };
  }, [confirmed, setLocation]);

  if (stage === 'confirm') {
    return (
      <PageContainer>
        <StatusCard
          icon={MailCheck}
          title="Continue to ReActor"
          description="Continue to verify your email link and set up or recover your account."
        >
          <Button onClick={() => { setStage('processing'); setConfirmed(true); }}>Continue</Button>
        </StatusCard>
      </PageContainer>
    );
  }

  if (stage === 'processing') {
    return (
      <PageContainer>
        <StatusCard
          icon={Loader2}
          title="Verifying your email link…"
          description="One moment while we complete your sign-in."
          loading
        />
      </PageContainer>
    );
  }

  if (stage === 'completed') {
    return (
      <PageContainer>
        <StatusCard
          icon={MailCheck}
          title="Verification complete"
          description="You’re all set! Redirecting to the app…"
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <StatusCard
        icon={TriangleAlert}
        title="We couldn’t verify your link"
        description={errorMessage ?? 'Please try again or contact support.'}
        variant="destructive"
      >
        <Button onClick={() => setLocation('/login')}>Back to login</Button>
      </StatusCard>
    </PageContainer>
  );
}


function PageContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      {children}
    </div>
  );
}

interface StatusCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  variant?: 'default' | 'destructive';
  loading?: boolean;
  children?: React.ReactNode;
}

function StatusCard({ icon: Icon, title, description, variant = 'default', loading, children }: StatusCardProps) {
  const iconClass = useMemo(() => {
    if (loading) return 'text-blue-600';
    return variant === 'destructive' ? 'text-red-600' : 'text-blue-600';
  }, [loading, variant]);

  return (
    <Card className="max-w-md w-full">
      <CardHeader className="text-center space-y-2">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
          <Icon className={`h-6 w-6 ${loading ? 'animate-spin' : ''} ${iconClass}`} />
        </div>
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      {children && (
        <CardContent className="flex flex-col gap-3 text-center">
          {children}
        </CardContent>
      )}
    </Card>
  );
}
